// SceneColorGrade — per-scene color grading + transition polish.
// Lives in the DOM (outside the R3F Canvas) inside the .planet-scene
// wrapper, so it can:
//
//   1) Apply each scene's CSS `filter` (subtle saturate/brightness/
//      hue-rotate from planetSections.sceneFilter) to the canvas, with a
//      long transition for a smooth grade cross-fade.
//   2) Paint a very subtle tint overlay (--scene-tint) using only the
//      existing palette (mint / gold / warm white).
//   3) Flash a brief 200ms vignette when the scene boundary is crossed.
//
// All values are driven off window.__planetSceneIndex, polled at the
// same cadence as the rest of the HUD (200ms) to avoid per-frame React
// churn.

import { useEffect, useRef, useState } from 'react';
import { SCENES } from './planetSections';

export function SceneColorGrade() {
  const [idx, setIdx] = useState(0);
  const [transitioning, setTransitioning] = useState(false);
  const transTimer = useRef<number | null>(null);

  useEffect(() => {
    if (typeof window === 'undefined') return;
    let last = window.__planetSceneIndex ?? 0;
    setIdx(last);
    const id = window.setInterval(() => {
      const cur = window.__planetSceneIndex ?? 0;
      if (cur !== last) {
        last = cur;
        setIdx(cur);
        // Brief 200ms vignette flash on boundary crossing.
        setTransitioning(true);
        if (transTimer.current !== null) window.clearTimeout(transTimer.current);
        transTimer.current = window.setTimeout(
          () => setTransitioning(false),
          200,
        );
      }
    }, 120);
    return () => {
      window.clearInterval(id);
      if (transTimer.current !== null) window.clearTimeout(transTimer.current);
    };
  }, []);

  // The canvas, typography and UI chrome all take their color from the
  // active chapter. This keeps the large color fields intentional.
  useEffect(() => {
    if (typeof document === 'undefined') return;
    const scene = SCENES[idx] ?? SCENES[0];
    const root = document.documentElement;
    root.style.setProperty('--planet-scene-filter', scene.sceneFilter);
    root.style.setProperty('--scene-base', scene.baseColor);
    root.style.setProperty('--scene-ink', scene.inkColor);
    root.style.setProperty('--scene-signal', scene.signalColor);
    root.style.setProperty('--bg', scene.baseColor);
    root.style.setProperty('--text-primary', scene.inkColor);
    root.style.setProperty('--text-secondary', `color-mix(in srgb, ${scene.inkColor} 72%, transparent)`);
    root.style.setProperty('--text-muted', `color-mix(in srgb, ${scene.inkColor} 48%, transparent)`);
    root.style.setProperty('--accent', scene.inkColor);
    root.style.setProperty('--accent-cool', scene.signalColor);
    root.style.setProperty('--accent-link', scene.signalColor);
  }, [idx]);

  return (
    <>
      <div
        className="planet-tint"
        data-scene={SCENES[idx]?.key ?? 'intro'}
        aria-hidden="true"
      />
      <div
        className={['planet-vignette', transitioning ? 'is-active' : '']
          .filter(Boolean)
          .join(' ')}
        aria-hidden="true"
      />
    </>
  );
}

export default SceneColorGrade;
